import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Brain, Save, Check, AlertCircle, Key } from "lucide-react";

const PROVIDER_META = {
    minimax: { label: "Minimax 2.7", badge: "Khuyến nghị", color: "gold" },
    openrouter: { label: "OpenRouter", badge: "Đa model", color: "navy" },
    chatgpt_api: { label: "ChatGPT API (OpenAI)", badge: "OpenAI", color: "navy" },
    gemini: { label: "Gemini 3 Pro", badge: "Qua OpenRouter", color: "navy" },
    chatgpt_oauth: { label: "ChatGPT OAuth", badge: "Sắp ra mắt", color: "slate", disabled: true },
};

export default function AdminAISettings() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");
    const [keys, setKeys] = useState({});       // local input for new API keys

    useEffect(() => {
        api.get("/admin/settings/ai-providers").then((r) => { setData(r.data); setLoading(false); })
            .catch((e) => { setErr(formatApiError(e.response?.data?.detail)); setLoading(false); });
    }, []);

    const updateProvider = (pid, patch) => {
        setData({ ...data, providers: { ...data.providers, [pid]: { ...data.providers[pid], ...patch } } });
    };

    const save = async () => {
        setSaving(true); setErr(""); setMsg("");
        // Merge any newly entered keys
        const providers = { ...data.providers };
        for (const [pid, k] of Object.entries(keys)) {
            if (k) providers[pid] = { ...(providers[pid] || {}), api_key: k };
        }
        try {
            await api.put("/admin/settings/ai-providers", { active: data.active, providers });
            setMsg("Đã lưu cấu hình AI.");
            setKeys({});
            const r = await api.get("/admin/settings/ai-providers");
            setData(r.data);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8"><div className="card h-64 shimmer" /></div>;

    return (
        <div className="p-6 lg:p-10 max-w-5xl">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-widest text-[var(--gold-deep)] font-bold flex items-center gap-2"><Brain className="w-3 h-3" /> Cấu hình AI</div>
                    <h1 className="font-heading text-3xl font-bold mt-1">AI Providers</h1>
                    <p className="text-slate-600 mt-1">Quản lý các nhà cung cấp AI dùng để chấm tự luận và sinh nội dung.</p>
                </div>
                <button onClick={save} disabled={saving} className="btn-gold" data-testid="save-ai-settings">
                    <Save className="w-4 h-4" /> {saving ? "Đang lưu…" : "Lưu cấu hình"}
                </button>
            </div>

            {msg && <div className="text-emerald-700 text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 flex items-center gap-2"><Check className="w-4 h-4" /> {msg}</div>}
            {err && <div className="text-[var(--red)] text-sm bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {err}</div>}

            <div className="card p-6 mb-6">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Provider đang dùng (cho mọi cuộc thi & AI generate)</div>
                <select value={data.active} onChange={(e) => setData({ ...data, active: e.target.value })} className="input max-w-md" data-testid="active-provider-select">
                    {Object.entries(PROVIDER_META)
                        .filter(([, m]) => !m.disabled)
                        .map(([pid, m]) => <option key={pid} value={pid}>{m.label}</option>)}
                </select>
            </div>

            <div className="space-y-4">
                {Object.entries(PROVIDER_META).map(([pid, meta]) => {
                    const cfg = data.providers?.[pid] || {};
                    return (
                        <div key={pid} className={`card p-6 ${meta.disabled ? "opacity-60" : ""}`} data-testid={`provider-card-${pid}`}>
                            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="font-heading text-lg font-bold">{meta.label}</h3>
                                        <span className={`badge badge-${meta.color === "gold" ? "gold" : meta.color === "slate" ? "slate" : "navy"}`}>{meta.badge}</span>
                                        {cfg.api_key_set && <span className="badge badge-success"><Check className="w-3 h-3" /> Đã có key</span>}
                                        {data.active === pid && <span className="badge badge-cyan">ĐANG DÙNG</span>}
                                    </div>
                                    <div className="text-xs text-slate-500 font-mono">{pid}</div>
                                </div>
                            </div>

                            {!meta.disabled && (
                                <div className="grid sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs uppercase tracking-widest text-slate-500 font-bold mb-1.5">Model</label>
                                        <input className="input" value={cfg.model || ""} onChange={(e) => updateProvider(pid, { model: e.target.value })} placeholder="VD: MiniMax-M2.7" data-testid={`${pid}-model-input`} />
                                    </div>
                                    <div>
                                        <label className="block text-xs uppercase tracking-widest text-slate-500 font-bold mb-1.5">Base URL</label>
                                        <input className="input font-mono text-xs" value={cfg.base_url || ""} onChange={(e) => updateProvider(pid, { base_url: e.target.value })} data-testid={`${pid}-baseurl-input`} />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className="block text-xs uppercase tracking-widest text-slate-500 font-bold mb-1.5 flex items-center gap-1"><Key className="w-3 h-3" /> API Key (chỉ nhập khi muốn cập nhật)</label>
                                        <input type="password" className="input font-mono" value={keys[pid] || ""} onChange={(e) => setKeys({ ...keys, [pid]: e.target.value })} placeholder={cfg.api_key_set ? "•••••••••• (đã có, để trống giữ nguyên)" : "Dán API key vào đây"} data-testid={`${pid}-apikey-input`} />
                                    </div>
                                </div>
                            )}
                            {meta.disabled && cfg.note && <div className="text-sm text-slate-500 italic">{cfg.note}</div>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
